<template>
  <div class="spoiler-section">
    <SrpDetailsSummary class="spoiler-section__details-summary" v-model:is-opened="isOpened" :transition-duration="150">
      <template #heading="{ isOpened }">
        <div
          :class="{
            'global-h3': true,
            'spoiler-section__summary': true,
            'spoiler-section__summary--with-bottom-padding': !isOpened,
          }"
        >
          <span><span>Content Uses</span></span>
        </div>
      </template>
      <template #details>
        <div class="spoiler-section__content">
          <!-- Social Posts Section -->
          <template v-if="customerSummary?.socialPosts?.length > 0">
            <h4 class="global-h4" style="margin-bottom: 12px">
              <span>Atlas Social Posts</span>
            </h4>
            <div class="social-posts-list" style="padding-bottom: 20px">
              <SocialPostPreview
                class="social-posts-list__post"
                v-for="socialPost in customerSummary.socialPosts"
                :key="socialPost.id"
                :assetIds="socialPost.assetIds"
                :caption="socialPost.caption || ''"
                :onlyAtlasReadonlyFieldsVisible="true"
                :isWithTextarea="false"
                :isWithShareAndDislikeButtons="false"
                :fancyboxKey="socialPost.id"
                :post="socialPost"
              />
            </div>
            <p style="margin-bottom: 12px">
              <template v-if="customerSummary.isAtlasEnabled"> Social Posts have been added to your calendar for the coming weeks. Review and Approve these anytime! </template>
              <template v-else> Easily add these posts to your social channels! </template>
            </p>
            <SrpButton
              tag="RouterLink"
              size="small"
              :to="{
                name: customerSummary.isAtlasEnabled ? 'CalendarPosts' : 'AtlasOptInLanding',
                params: { customerId: communityId },
              }"
            >
              {{ customerSummary.isAtlasEnabled ? "Review your Calendar" : "Try Atlas Today" }}
            </SrpButton>

            <div style="padding-top: 40px; margin-bottom: 50px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>
          <!-- Adventures Section -->
          <div v-if="isLoading" style="padding: 20px 0">Loading...</div>
          <template v-else-if="customerSummary?.adventures.length > 0">
            <h4 class="global-h4" style="margin-bottom: 12px">
              <span>Adventures</span>
            </h4>
            <div style="padding-bottom: 10px">
              <ul style="width: 100%; padding: 0; margin: 0; display: flex; flex-wrap: wrap; list-style: none">
                <ItineraryDetailsSnippet
                  class=""
                  v-for="(itinerary, index) in customerSummary?.adventures"
                  :key="itinerary.id"
                  :itinerary="itinerary"
                  :style="{ marginBottom: index !== customerSummary?.adventures.length - 1 ? '25px' : 0 }"
                  target="_blank"
                  :enableStopDetailsSection="true"
                  :isEditingAllowed="true"
                  :defaultStopsOpen="false"
                  :hideEditGuidance="true"
                />
              </ul>
              <SrpButton v-if="!collabInput.collaboration?.completedDate && $route.query.advdelete === 'true'" @click="deleteAdventures" customColor="#d6d3e8" size="tiny" style="margin-top: 30px"
                >Delete Adventures</SrpButton
              >
            </div>
          </template>

          <template v-else>
            <h4 class="global-h4" style="margin-bottom: 12px">Adventure Creation Pending</h4>
            <SrpButton @click="isGenerateModalVisible = true" color="gray" fill="outlined" style="margin-top: 10px">Gen Adventures</SrpButton>
          </template>
        </div>
      </template>
    </SrpDetailsSummary>

    <GenerateAdventuresModal v-model:isVisible="isGenerateModalVisible" @generate="handleGenerateAdventures" />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import axios from "axios";

// Types
import { CollabInput } from "@contracts/collab";
import { Itinerary } from "@contracts/itinerary";
import { SocialMediaPostData } from "@contracts/shareStop";

interface CustomerSummary {
  adventures: Array<Itinerary>;
  socialPosts: Array<SocialMediaPostData>;
  isAtlasEnabled: boolean;
}

// Components
import ItineraryDetailsSnippet from "@components/ItineraryDetailsSnippet.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import GenerateAdventuresModal from "@components/Modals/GenerateAdventuresModal.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";

export default defineComponent({
  name: "CollabContentUsage",

  components: {
    ItineraryDetailsSnippet,
    SrpDetailsSummary,
    SrpButton,
    GenerateAdventuresModal,
    SocialPostPreview,
  },

  props: {
    communityId: { type: String, required: true },
    collabInput: { type: Object as () => CollabInput, required: true },
    collabIndex: { type: Number, required: true },
  },

  data() {
    return {
      globalLog: inject("globalLog") as any,

      isOpened: true,
      isLoading: false,
      isGenerateModalVisible: false,

      customerSummary: null as CustomerSummary,
    };
  },

  watch: {
    // Resets the state since they changed collabs
    collabIndex: function (newVal, oldVal) {
      this.globalLog.info(`collabIndex changed: new=${newVal}, old=${oldVal}`);
      this.onLoad();
    },
  },

  async mounted() {
    await this.onLoad();
  },

  methods: {
    async onLoad() {
      this.isLoading = true;
      this.customerSummary = await this.loadCustomerSummary();
      this.isLoading = false;
    },
    async loadCustomerSummary(): Promise<CustomerSummary> {
      // Note: Some of this is for testing AI gen on old collabs, long-term this can probably be simplified a bit
      const status = this.collabInput.collaboration?.completedDate ? "Approved" : "NotApplicable"; // long-term only Approved is needed here
      let uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/customer-summary?onlyCustomerOwned=true&status=${status}`;
      const { data } = await axios.get<CustomerSummary>(uri);

      return data;
    },
    async handleGenerateAdventures({ count, guidance }: { count: number; guidance: string }) {
      this.isLoading = true;
      const uri = `${import.meta.env.VITE_API_URL}/adventure-gen/${this.communityId}/collabs/${this.collabInput.id}`;
      const payload = {
        adventureCountToCreate: count,
        customerGuidance: guidance,
        save: true,
      };
      await axios.post(uri, payload);
      this.customerSummary = await this.loadCustomerSummary();
      this.isLoading = false;
    },
    async deleteAdventures() {
      this.isLoading = true;
      const uri = `${import.meta.env.VITE_API_URL}/adventure-gen/${this.communityId}/collabs/${this.collabInput.id}`;
      await axios.delete(uri);
      this.customerSummary = await this.loadCustomerSummary();
      this.isLoading = false;
    },
  },
});
</script>

<style scoped lang="scss">
@import "./AllCollabsV2.scss";
@import "@/scss/screen-size-ranges.scss";

.social-posts-list {
  gap: $desktop-wide-grid-gap-width;
  display: flex;
  flex-wrap: wrap;

  &__post {
    width: calc(33.3% - $desktop-wide-grid-gap-width * 2 / 3);
    flex-shrink: 0;
  }
}

// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .social-posts-list {
    gap: $laptop-grid-gap-width;

    &__post {
      width: calc(33.3% - $laptop-grid-gap-width * 2 / 3);
    }
  }
}

// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .social-posts-list {
    gap: $tablet-large-grid-gap-width;

    &__post {
      width: calc(33.3% - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}

// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .social-posts-list {
    gap: $tablet-grid-gap-width;

    &__post {
      width: calc(50% - $tablet-grid-gap-width * 1 / 2);
    }
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .social-posts-list {
    &__post {
      width: 100%;
    }
  }
}
</style>
